import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "src/components/card";
import { hrisConfigQueryOptions } from "src/api/mvte/hris-rep-query-options";
import { JobMaster } from "../types/JobMaster";
import { YellowTenstreetApplicant } from "../types/YellowTensreetApplicant";
import { Applicant } from "../types/Applicant";
import { YellowMarkedRaw } from "../types/YellowMarkRaw";

type ColumnDef<T> =
{
    key: keyof T;
    label: string;
};


const selectedRowColumns: ColumnDef<YellowMarkedRaw>[] =
[
    { key: "EmployeeJobMasterID", label: "Employee Job Master ID" },
    { key: "EmployeeUID", label: "Employee UID" },
    { key: "EmployeeCode", label: "Employee Code" },
    { key: "EmployerOfRecordID", label: "Employer of Record ID" },
    { key: "ClientCompanyID", label: "Client Company ID" },
    { key: "EmployeeType", label: "Employee Type" },
    { key: "JobCode", label: "Job Code" },
    { key: "JobTitle", label: "Job Title" },
    { key: "JobType", label: "Job Type" },
    { key: "SupervisorUID", label: "Supervisor UID" },
    { key: "HireDate", label: "Hire Date" },
    { key: "EmployeeStatus", label: "Employee Status" },
    { key: "DataSource", label: "Data Source" },
    { key: "CreatedAtUtc", label: "Created At (UTC)" },
    { key: "UpdatedAtUtc", label: "Updated At (UTC)" },
    { key: "JobMasterID", label: " Job Master ID" },
];

const jobMasterColumns: ColumnDef<JobMaster>[] =
[
    { key: "JobMasterID", label: "Job Master ID" },
    { key: "EmployeeUid", label: "Employee UID" },
    { key: "EmployeeCode", label: "Employee Code" },
    { key: "EmployerOfRecordID", label: "Employer Of Record ID" },
    { key: "ClientCompanyID", label: "Client Company ID" },
    { key: "WorksiteID", label: "Worksite ID" },
    { key: "JobPositionID", label: "Job Position ID" },
    { key: "SupervisorUID", label: "Supervisor UID" },
    { key: "CreatedAtUtc", label: "Created At (UTC)" },
    { key: "UpdateAtUtc", label: "Updated At (UTC)" },
];


const applicantColumns: ColumnDef<Applicant>[] =
[
    { key: "ApplicantID", label: "Applicant ID" },
    { key: "EmployerOfRecordID", label: "Employer Of Record ID" },
    { key: "ClientCompanyID", label: "Client Company ID" },
    { key: "HRStatus", label: "HR Status" },
    { key: "EmployeeCode", label: "Employee Code" },
    { key: "ApplicantUID", label: "Applicant UID" },
    { key: "TenstreetApplicantID", label: "Tenstreet Applicant ID" },
    { key: "FirstName", label: "First Name" },
    { key: "MiddleName", label: "Middle Name" },
    { key: "LastName", label: "Last Name" },
    { key: "StageRefID", label: "Stage Ref ID" },
    { key: "EmployeeType", label: "Employee Type" },
    { key: "WorksiteID", label: "Worksite ID" },
];

const tenstreetColumns: ColumnDef<YellowTenstreetApplicant>[] =
[
    { key: "ID", label: "ID" },
    { key: "EmployeeCode", label: "Employee Code" },
    { key: "FirstName", label: "First Name" },
    { key: "MiddleName", label: "Middle Name" },
    { key: "LastName", label: "Last Name" },
    { key: "EmployerOfRecordName", label: "Employer Of Record" },
    { key: "ClientCompanyName", label: "Client Company" },
    { key: "Worksite", label: "Worksite" },
    { key: "WorkSiteCode", label: "Worksite Code" },
    { key: "EmployeeType", label: "Employee Type" },
    { key: "PositionApplied", label: "Position Applied" },
    { key: "SupervisorName", label: "Supervisor Name" },
    { key: "SupervisorUid", label: "Supervisor UID" },
];

function buildFullName(first?: string, middle?: string, last?: string): string
{
    return [first, middle, last].filter((part) => !!part && part.trim() !== "").join(" ");
}

function YellowEmployeeDetails()
{
    const navigate = useNavigate();
    const location = useLocation();
    const selectedRow = (location.state as { selectedRow?: YellowMarkedRaw } | null)?.selectedRow;
    const employeeUID = selectedRow?.EmployeeUID;


    const jobMasterQuery = useQuery(
    {
        ...hrisConfigQueryOptions.yellowMarkedJobMasterQueryOptions({ params: { EmployeeUid: employeeUID?.toString()! } }),
        enabled: !!employeeUID,
    });

    const applicantQuery = useQuery(
    {
        ...hrisConfigQueryOptions.yellowMarkedApplicantQueryOptions({ params: { ApplicantUID: employeeUID?.toString()! } }),
        enabled: !!employeeUID,
    });

    const stageRefID = (applicantQuery.data as Applicant | undefined)?.StageRefID;

    const tenstreetQuery = useQuery(
    {
        ...hrisConfigQueryOptions.yellowMarkedTenstreetApplicantQueryOptions({ params: { StageRefID: stageRefID! } }),
        enabled: !!stageRefID,
    });

    const applicantData = applicantQuery.data as Applicant | undefined;
    const tenstreetData = tenstreetQuery.data as YellowTenstreetApplicant | undefined;

    const fullName =
        buildFullName(applicantData?.FirstName, applicantData?.MiddleName, applicantData?.LastName) ||
        buildFullName(tenstreetData?.FirstName, tenstreetData?.MiddleName, tenstreetData?.LastName);

    const isNameLoading = applicantQuery.isLoading || (!!stageRefID && tenstreetQuery.isLoading);
    const nameDisplay = isNameLoading ? "Loading…" : (fullName || "Unknown Employee");

    return (
        <div>
            <div style={titleBlockStyle}>
                <div style={titleStyle}>{nameDisplay}</div>
                <div style={subtitleStyle}>{employeeUID ?? "—"}</div>
            </div>

            <Card>
                <YellowDataTable<YellowMarkedRaw>
                    caption="Selected Yellow Marked Employee"
                    columns={selectedRowColumns}
                    data={selectedRow}
                    isLoading={false}
                    isError={false}
                    highlightEmptyKeys={["EmployeeUID", "EmployeeCode", "EmployerOfRecordID", "ClientCompanyID"]}
                />
            </Card>

            <Card>
                <YellowDataTable<JobMaster>
                    caption="HR Engauge Job Master"
                    columns={jobMasterColumns}
                    data={jobMasterQuery.data as JobMaster | undefined}
                    isLoading={jobMasterQuery.isLoading}
                    isError={jobMasterQuery.isError}
                    highlightEmptyKeys={["EmployeeUid", "EmployeeCode", "EmployerOfRecordID", "ClientCompanyID"]}
                />
            </Card>

            <Card>
                <YellowDataTable<Applicant>
                    caption="HR Applicant Info"
                    columns={applicantColumns}
                    data={applicantData}
                    isLoading={applicantQuery.isLoading}
                    isError={applicantQuery.isError}
                    highlightEmptyKeys={["HRStatus"]}
                />
            </Card>

            <Card>
                <YellowDataTable<YellowTenstreetApplicant>
                    caption="Stage Tenstreet Applicant"
                    columns={tenstreetColumns}
                    data={tenstreetData}
                    isLoading={tenstreetQuery.isLoading}
                    isError={tenstreetQuery.isError}
                />
            </Card>
                <div style={headerRowStyle}>
                <button onClick={() => navigate(-1)} style={backButtonStyle}>
                    ← Back
                </button>
            </div>
        </div>
    );
}

function YellowDataTable<T extends Record<string, any>>(
{
    caption,
    columns,
    data,
    isLoading,
    isError,
    highlightEmptyKeys,
} : 
{
    caption: string;
    columns: ColumnDef<T>[];
    data: T | T[] | undefined;
    isLoading: boolean;
    isError: boolean;
    highlightEmptyKeys?: (keyof T)[];
})
{
    const rows = data ? (Array.isArray(data) ? data : [data]) : [];
    const showPlaceholder = !isLoading && !isError && rows.length === 0;

    return (
        <div>
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "auto",
                    border: "1px solid #000",
                }}
            >
                <caption style={{ textAlign: "left", fontWeight: 'bold', paddingBottom: '10px'}}>{caption}</caption>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={String(col.key)} style={thStyle}>
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading && (
                        <tr>
                            <td style={tdStyle} colSpan={columns.length}>
                                Loading…
                            </td>
                        </tr>
                    )}
                    {isError && (
                        <tr>
                            <td style={tdStyle} colSpan={columns.length}>
                                Failed to load data — backend endpoint may not be available yet.
                            </td>
                        </tr>
                    )}
                    {showPlaceholder && (
                        <tr>
                            <td style={tdStyle} colSpan={columns.length}>
                                No data available
                            </td>
                        </tr>
                    )}
                    {rows.map((row, i) => (
                        <tr key={i}>
                            {columns.map((col) => {
                                const shouldHighlight =
                                    !!highlightEmptyKeys?.includes(col.key);
                                return (
                                    <td
                                        key={String(col.key)}
                                        style={shouldHighlight ? tdHighlightStyle : tdStyle}
                                    >
                                        {String(row[col.key] ?? "")}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export { YellowEmployeeDetails };

const thStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 600,
};

const tdStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
};

const tdHighlightStyle: React.CSSProperties =
{
    ...tdStyle,
    backgroundColor: "#fff3b0",
};

const headerRowStyle: React.CSSProperties =
{
    marginBottom: "12px",
};

const backButtonStyle: React.CSSProperties =
{
    padding: "4px 12px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};

const titleBlockStyle: React.CSSProperties =
{
    marginBottom: "20px",
};

const titleStyle: React.CSSProperties =
{
    fontSize: "22px",
    fontWeight: 700,
};

const subtitleStyle: React.CSSProperties =
{
    fontSize: "14px",
    fontWeight: 400,
    color: "#555",
};